import React, {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
} from "react";
import { useLoadScript } from "@react-google-maps/api";
import { Libraries } from "@react-google-maps/api/dist/utils/make-load-script-url";
import Geocode from "react-geocode";
import { StreetSweepingSchedule, GoogleMapCoordinate } from "../models";
import { formattedStreetAddress } from "../utils";

interface GoogleMapsContextProps {
  readonly isGoogleMapLoaded: boolean;
  geocodeStreet: (
    street: StreetSweepingSchedule
  ) => Promise<GoogleMapCoordinate>;
}

export const GoogleMapsContext = createContext<GoogleMapsContextProps>({
  isGoogleMapLoaded: false,
  geocodeStreet: () => Promise.resolve({ lat: null, lng: null }),
});

interface GoogleMapsContextProviderProps {
  readonly googleMapsApiKey: string;
  readonly children: ReactNode;
}

export function GoogleMapsContextProvider({
  googleMapsApiKey,
  children,
}: GoogleMapsContextProviderProps): ReactElement {
  const geocodeStreet = useCallback(
    async (streetSchedule: StreetSweepingSchedule) => {
      if (streetSchedule) {
        try {
          Geocode.setApiKey(googleMapsApiKey);
          const streetAddress = formattedStreetAddress(streetSchedule);
          const response = await Geocode.fromAddress(streetAddress);
          const { lat, lng } = await response.results[0].geometry.location;
          return { lat, lng };
        } catch (error) {
          console.warn("Error geocoding address: ", streetSchedule, error);
        }
      }
      return { lat: null, lng: null };
    },
    [googleMapsApiKey]
  );

  const GOOGLE_MAPS_LIBRARIES: Libraries = ["places", "geometry"];
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleMapsApiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
    region: "AU",
  });

  return (
    <GoogleMapsContext.Provider
      value={{ isGoogleMapLoaded: isLoaded, geocodeStreet }}
    >
      {children}
    </GoogleMapsContext.Provider>
  );
}
